import type { ComponentType } from "react";

import type { ToolArtifact } from "../aui/convert";
import type { ToolUiKind, WireChoice } from "../protocol/protocol";
import { DECISION_CARD } from "./decisions/DecisionCard";
import { OFFER_CARD } from "./decisions/OfferCard";
import { REFINANCE_CARD } from "./decisions/RefinanceCard";
// MerchantFlowCard draws ApprovalCard, which looks cards up here: a cycle that is safe because
// neither side reads the other at load, only at render. Keep it so: no top-level use of
// NATIVE_CARDS or nativeChoiceCard in ApprovalCard.
import { MERCHANT_FLOW_CARD } from "./MerchantFlowCard";
import { MERCHANT_TASKS_CARD } from "./MerchantTasksCard";
import { NEW_APPLICATION_CARD } from "./newApplication/NewApplicationCard";

/** What a native card gets: the call as the thread holds it. */
export interface NativeCardProps {
  /** The call's arguments (a stored call's; a live one has none yet). */
  args: Record<string, unknown>;
  /** The tool's JSON result once its message is stored; undefined until then. */
  result: unknown;
  /** The call's line, state and choice, as a plain tool part gets them. */
  artifact: ToolArtifact;
}

export interface NativeCard {
  Card: ComponentType<NativeCardProps>;
  /** Whether this call draws as the card; otherwise as it would with none. Default: always. */
  shows?: (artifact: ToolArtifact, result: unknown) => boolean;
  /**
   * The card of a choice whose tool card has this kind (`choice.card.kind`), wherever the choice
   * shows (an approval no tool part holds included), when `reads` says this build reads it.
   */
  choice?: {
    Card: ComponentType<{ choiceRef: string }>;
    reads: (choice: WireChoice) => boolean;
  };
}

/**
 * The web's own React cards, keyed by the tool's ToolUi kind (`ui_kind` on the call). A call whose
 * kind is here draws as the card, ahead of its choice's card; any other draws as before (its line,
 * or its approval or pick card). A card that wants the call's line too renders `ToolLine` itself.
 */
export const NATIVE_CARDS: Partial<Record<ToolUiKind, NativeCard>> = {
  merchant_tasks: MERCHANT_TASKS_CARD,
  application_decision: DECISION_CARD,
  offer: OFFER_CARD,
  refinance: REFINANCE_CARD,
  merchant_flow: MERCHANT_FLOW_CARD,
  new_application: NEW_APPLICATION_CARD,
};

/** The native card a choice draws as, by its tool card's kind; null for the approval view. */
export function nativeChoiceCard(
  choice: WireChoice | null | undefined
): ComponentType<{ choiceRef: string }> | null {
  const kind = choice?.card?.kind as ToolUiKind | undefined;
  if (!choice || !kind) return null;
  const entry = NATIVE_CARDS[kind]?.choice;
  return entry?.reads(choice) ? entry.Card : null;
}

/** The native card a tool part draws as, if any. */
export function nativeCard(artifact: ToolArtifact | undefined, result: unknown): NativeCard | null {
  if (!artifact?.uiKind) return null;
  const card = NATIVE_CARDS[artifact.uiKind];
  return card && (card.shows?.(artifact, result) ?? true) ? card : null;
}
